import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Assistant, DialogStep } from "@/types/video-call";

interface AssistantSelectionDialogProps {
  open: boolean;
  assistants: Assistant[];
  selectedAssistantId: string;
  loadingAssistants: boolean;
  dialogStep: DialogStep;
  onAssistantChange: (assistantId: string) => void;
  onNextStep: () => void;
  onClose: () => void;
}

export const AssistantSelectionDialog: React.FC<AssistantSelectionDialogProps> = ({
  open,
  assistants,
  selectedAssistantId,
  loadingAssistants,
  dialogStep,
  onAssistantChange,
  onNextStep,
  onClose,
}) => {
  const canProceed = selectedAssistantId && dialogStep === "assistant";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select Assistant</DialogTitle>
          <DialogDescription>
            Choose the AI assistant to handle this video call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {loadingAssistants ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner text="Loading assistants..." />
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">Assistant</label>
              <Select value={selectedAssistantId} onValueChange={onAssistantChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an assistant" />
                </SelectTrigger>
                <SelectContent>
                  {assistants.map((assistant) => (
                    <SelectItem key={assistant.id} value={assistant.id}>
                      <div>
                        <div className="font-medium">{assistant.name}</div>
                        {assistant.description && (
                          <div className="text-sm text-muted-foreground">
                            {assistant.description}
                          </div>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onNextStep} disabled={!canProceed}>
            Next: Configure Monitoring
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};